import React, { useState } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Badge,
  DeviceHub,
  Logout,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
}

const SIDEBAR_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 80;

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, variant = 'permanent' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [collapsed, setCollapsed] = useState(false);

  // Determine current page context
  const isCustomersPage = location.pathname === '/customers' || location.pathname.startsWith('/customers/');
  const isDistributedAgentsPage = location.pathname === '/distributed-agents';

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    if (isMobile) {
      onClose();
    }
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo and Title */}
      <Box sx={{ 
        p: collapsed ? 1 : 2, 
        display: 'flex', 
        flexDirection: collapsed ? 'row' : 'column',
        alignItems: 'center', 
        minHeight: collapsed ? 80 : 100,
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'relative'
      }}>
        {!collapsed && (
          <>
            <Box sx={{ mb: 1.5 }}>
              <img 
                src="/NTT_company_logo.png" 
                alt="NTT Logo" 
                style={{ height: '48px', width: 'auto' }} 
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.2 }}>
                Network Discovery
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Platform
              </Typography>
            </Box>
          </>
        )}
        {!isMobile && (
          <IconButton 
            onClick={toggleCollapsed} 
            size="small"
            sx={{ 
              position: 'absolute',
              top: collapsed ? '50%' : 8,
              right: 8,
              transform: collapsed ? 'translateY(-50%)' : 'none'
            }}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
      </Box>

      {/* Main Navigation */}
      <List sx={{ flexGrow: 1, pt: 1 }}>
        {/* Customers */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation('/customers')}
            selected={isCustomersPage}
            sx={{
              minHeight: 48,
              px: collapsed ? 1.5 : 2,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white'
                },
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
              <Badge />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Customers" />}
          </ListItemButton>
        </ListItem>

        {/* Distributed Agents */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation('/distributed-agents')}
            selected={isDistributedAgentsPage}
            sx={{
              minHeight: 48,
              px: collapsed ? 1.5 : 2,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white'
                },
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
              <DeviceHub />
            </ListItemIcon>
            {!collapsed && <ListItemText primary="Distributed Agents" />}
          </ListItemButton>
        </ListItem>

      </List>

      {/* Bottom Section - Logout */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                minHeight: 48,
                px: collapsed ? 1.5 : 2,
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white'
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                <Logout />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Logout" />}
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );

  if (collapsed && !isMobile) {
    return (
      <Box sx={{ position: 'relative', display: 'flex' }}>
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarWidth,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider'
            },
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Collapsed Logo */}
            <Box sx={{ 
              p: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: 80,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Tooltip title="Network Discovery Platform" placement="right">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <img 
                    src="/web-app-manifest-192x192.png" 
                    alt="Network Discovery Platform" 
                    style={{ height: '32px', width: '32px', marginBottom: '4px' }} 
                  />
                </Box>
              </Tooltip>
            </Box>

            {/* Collapsed Navigation */}
            <List sx={{ flexGrow: 1, pt: 1 }}>
              <Tooltip title="Customers" placement="right">
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation('/customers')}
                    selected={isCustomersPage}
                    sx={{
                      minHeight: 48,
                      px: 1.5,
                      justifyContent: 'center',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }
                    }}
                  >
                    <Badge />
                  </ListItemButton>
                </ListItem>
              </Tooltip>

              <Tooltip title="Distributed Agents" placement="right">
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation('/distributed-agents')}
                    selected={isDistributedAgentsPage}
                    sx={{
                      minHeight: 48,
                      px: 1.5,
                      justifyContent: 'center',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'primary.dark'
                        }
                      }
                    }}
                  >
                    <DeviceHub />
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            </List>

            {/* Collapsed Logout */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
              <List>
                <Tooltip title="Logout" placement="right">
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={handleLogout}
                      sx={{
                        minHeight: 48,
                        px: 1.5,
                        justifyContent: 'center',
                        '&:hover': {
                          backgroundColor: 'error.light',
                          color: 'white'
                        }
                      }}
                    >
                      <Logout />
                    </ListItemButton>
                  </ListItem>
                </Tooltip>
              </List>
            </Box>
          </Box>
        </Drawer>
        
        {/* Dedicated Chevron Tab */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: sidebarWidth,
            width: 24,
            height: 40,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: 'none',
            borderRadius: '0 8px 8px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 1,
            zIndex: 1201,
            '&:hover': {
              backgroundColor: 'grey.100'
            }
          }}
          onClick={toggleCollapsed}
        >
          <ChevronRight fontSize="small" />
        </Box>
      </Box>
    );
  }

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: sidebarWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: sidebarWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider'
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
